import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import SyncStatusCard from './SyncStatusCard';
import payrollSyncMonitoringService from '../../../services/payrollSyncMonitoringService';

const PayrollSyncDashboard = () => {
  const [syncStats, setSyncStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    loadSyncData();
    
    // Subscribe to real-time updates
    const channel = payrollSyncMonitoringService?.subscribeToSyncUpdates(() => {
      loadSyncData();
    });
    
    return () => {
      payrollSyncMonitoringService?.unsubscribeFromSyncUpdates(channel);
    };
  }, []);

  const loadSyncData = async () => {
    try {
      setLoading(true);
      const data = await payrollSyncMonitoringService?.getAllSyncStatistics();
      setSyncStats(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSyncData();
  };

  const getOverallHealth = () => {
    if (syncStats?.length === 0) return 'unknown';
    const hasError = syncStats?.some(s => s?.connectionHealth === 'error');
    const hasWarning = syncStats?.some(s => s?.connectionHealth === 'warning');
    const allHealthy = syncStats?.every(s => s?.connectionHealth === 'healthy');
    
    if (hasError) return 'error';
    if (hasWarning) return 'warning';
    if (allHealthy) return 'healthy';
    return 'unknown';
  };

  const getTotalUnresolvedConflicts = () => {
    return syncStats?.reduce((sum, stat) => sum + (stat?.unresolvedConflicts || 0), 0);
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy': return { name: 'CheckCircle', color: 'var(--color-success)' };
      case 'warning': return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'error': return { name: 'XCircle', color: 'var(--color-error)' };
      default: return { name: 'HelpCircle', color: 'var(--color-muted-foreground)' };
    }
  };

  const overallHealth = getOverallHealth();
  const healthIcon = getHealthIcon(overallHealth);
  const totalConflicts = getTotalUnresolvedConflicts();

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 w-full">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 w-full">
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Clock" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm break-words">Thank you for visiting. Pacific RBS Ltd is introducing full HR Management System for Private and Public Sector for 2026. This website is launching soon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon name="RefreshCw" size={20} color="var(--color-primary)" className="flex-shrink-0" />
          <h2 className="font-heading text-base md:text-lg font-semibold text-foreground truncate">
            Payroll Data Synchronization
          </h2>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-md transition-smooth"
          aria-label="Refresh sync status"
        >
          <Icon name="RefreshCw" size={16} color="var(--color-foreground)" />
          <span className="text-xs font-medium text-foreground hidden md:inline">Refresh</span>
        </button>
      </div>

      {/* Overall Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name={healthIcon?.name} size={18} color={healthIcon?.color} />
            <span className="caption text-sm text-muted-foreground">Overall Health</span>
          </div>
          <span className="text-2xl font-bold data-text text-foreground capitalize">
            {overallHealth}
          </span>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
            <span className="caption text-sm text-muted-foreground">Unresolved Conflicts</span>
          </div>
          <span className="text-2xl font-bold data-text text-foreground">
            {totalConflicts}
          </span>
        </div>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Database" size={18} color="var(--color-accent)" />
            <span className="caption text-sm text-muted-foreground">Active Providers</span>
          </div>
          <span className="text-2xl font-bold data-text text-foreground">
            {syncStats?.filter(s => s?.isActive)?.length} / {syncStats?.length}
          </span>
        </div>
      </div>

      {/* Provider Status Cards */}
      <div>
        <h3 className="font-body font-semibold text-sm text-foreground mb-3">
          Provider Connection Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {syncStats?.map((provider) => (
            <SyncStatusCard key={provider?.providerName} provider={provider} />
          ))}
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="caption">Last updated: {lastRefresh?.toLocaleTimeString('en-GB')}</span>
          <span className="caption">Auto-refresh enabled</span>
        </div>
      </div>
    </div>
  );
};

export default PayrollSyncDashboard;